/* eslint-disable react-refresh/only-export-components -- `wrapBareText` is deliberately colocated
   with `TranslationSafeText`. This file is the single documented home for Google-Translate
   text-node safety; splitting the helper into a sibling module would recreate exactly the
   scattering it exists to remove. */

/**
 * TranslationSafeText — the canonical home for Google-Translate text-node safety.
 *
 * This is a technical DOM/React-reconciliation workaround, not a design-system component. It
 * deliberately lives outside `@web-monorepo/dds`: it has no visual design, no tokens and no theme,
 * and a `DDS` prefix would misfile it as part of the Dojo Design System. The DDS component
 * conventions in `packages/dds/dds-component.mdc` — including the required `data-name` prop — apply
 * to design-system components and not to this package; `data-name` is still *accepted* and
 * forwarded like any other span prop, it is simply not mandatory on a styleless wrapper that
 * renders no interactive surface.
 *
 * ## What it is
 *
 * Internally *just* a `<span>`. No styles, no classes, no attributes of its own — it forwards
 * `className`, `style`, `ref` and every other span prop straight through, so it drops into an
 * existing tree without any visual or layout change. Its entire value is the element boundary it
 * puts between React and a bare text node, plus this documentation.
 *
 * ## Why it exists
 *
 * Google Translate (and other in-page translators) rewrites a text node by *replacing* it with an
 * injected `<font>` element that holds the translated text:
 *
 * ```html
 * <!-- before -->  <div>Dad</div>
 * <!-- after -->   <div><font style="vertical-align: inherit">Papá</font></div>
 * ```
 *
 * When the text node it moves is one React manages as its own child, React's next reconciliation
 * of that position calls `removeChild` on a node whose parent has changed and throws:
 *
 * ```
 * Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.
 * ```
 *
 * In Sentry these surface as `removeChild` / `AnnotatedDOMError` crashes — originally issues 391013,
 * 384579, 384542 and 384514 (WEBP-136), plus 314874 from the screen-reader live region. See
 * "Related instrumentation and tooling" below for what produces and triages them.
 *
 * Given an element whose *only* child is text, react-dom takes its `shouldSetTextContent` fast
 * path: it writes through the element's `textContent` instead of managing a child text node. The
 * translator's injected nodes are then overwritten harmlessly, and the node React owns — the
 * `<span>` — is never moved. That is the whole trick.
 *
 * ## We deliberately do NOT block translation
 *
 * There is no `notranslate` class, no `translate="no"`, and no opt-in suppression prop here, and
 * none should be added. The direction recorded on WEBP-136 (and seen again in TG-140) is explicit:
 * we do not want to prevent Google Translate from working. Families rely on it. This component
 * makes translation *survivable*, not impossible.
 *
 * If you genuinely need to keep a specific string untranslated — a proper noun, a login code, a
 * language name in its own language — that is a different, product-level decision. Use
 * `SkipTranslate` from `@web-monorepo/translation-runtime`, which is also a build-time marker for
 * our own auto-translate plugin. It is not a substitute for this component, and this component is
 * not a substitute for it.
 *
 * ## When to use it
 *
 * - A bare `string`/`number` child is about to be handed to something that will render it *as a
 *   text node React owns in a position React later updates*. The canonical case is a library
 *   portalling children into a node it does not otherwise re-render. radix's `SelectItemText` does
 *   exactly this — it renders the selected item's children into the trigger's value node through
 *   `createPortal`:
 *
 *   ```js
 *   // @radix-ui/react-select 1.2.2
 *   itemContext.isSelected && context.valueNode && !context.valueNodeHasChildren
 *     ? createPortal(itemTextProps.children, context.valueNode)
 *     : null;
 *   ```
 *
 *   With a bare string label (`<DDSSelectItem value="dad">Dad</DDSSelectItem>`) that portal renders
 *   a text node React owns directly inside `valueNode`; once a translator moves it under a `<font>`,
 *   the next reconciliation of the portal throws. See
 *   `packages/dds/src/components/Inputs/DDSSelect/DDSSelectRadixImpl.tsx`.
 * - An `aria-live` region whose contents React swaps over time (see
 *   `packages/dds/src/components/Misc/DDSScreenReaderAnnouncer/DDSScreenReaderAnnouncer.tsx`).
 * - Product copy that React re-renders in place and a translator is likely to have rewritten — for
 *   example message text that the product itself offers to translate
 *   (`packages/messaging/src/components/MessageList/MessageItem/MessageBubble.tsx`).
 * - You are writing a component that accepts `ReactNode` children and passes them somewhere you
 *   do not control. Use {@link wrapBareText} rather than wrapping unconditionally.
 *
 * ## Call sites usually need no comment
 *
 * `<TranslationSafeText>{value}</TranslationSafeText>` is its own pointer — a reader who wants the
 * "why" follows the symbol here. Do not put a comment next to a usage: naming the component and
 * adding `// See @web-monorepo/translation-safe-dom` beside it is pure redundancy.
 *
 * Exactly two kinds of site earn a one-line `// See @web-monorepo/translation-safe-dom`, because
 * the code there never names the primitive and a reader would otherwise have no thread to pull:
 *
 * - somewhere that only *observes* this crash class rather than preventing it — the telemetry
 *   annotator and the top-level error boundary listed below;
 * - a comment explaining a deliberate **absence**, i.e. why nearby text is intentionally not
 *   wrapped, which the code cannot show on its own (`DDSSelectNative.tsx`).
 *
 * If a hand-rolled `<span>` would otherwise need such a pointer, use this component instead and
 * drop the comment — adopting the primitive and deleting the prose are the same fix.
 *
 * ## When NOT to use it
 *
 * - **Around element children.** An element already gives React a stable boundary; an extra span
 *   only adds DOM and risks changing layout. {@link wrapBareText} returns element children by
 *   identity for this reason.
 * - **Inside `<option>`.** `<option>` only accepts text, so a single text child already goes
 *   through `shouldSetTextContent`; nothing can leave React holding a reparented text node.
 *   `DDSSelectNative.tsx` documents this at its own call site.
 * - **Blanket-wrapping every string in the app.** Static copy that React never re-renders in place
 *   is not on the crash path, and the extra spans are not free (`display: inline` boundaries can
 *   affect `text-overflow`, flex/grid child counting and `:first-child`-style selectors).
 * - **As a typography component.** It carries no text styles. Use `DDSBodyText` and friends.
 *
 * @example Wrapping a value that a third-party library will portal elsewhere
 * ```tsx
 * <SelectPrimitive.ItemText>{wrapBareText(children)}</SelectPrimitive.ItemText>
 * ```
 *
 * @example Direct use when you know the child is text
 * ```tsx
 * <TranslationSafeText className={styles.name}>{student.name}</TranslationSafeText>
 * ```
 *
 * ## Related instrumentation and tooling
 *
 * This module owns the *explanation* and the *fix*. Three other places participate in the crash's
 * lifecycle and deliberately stay where they are, because each is general-purpose and only counts
 * this crash class as its highest-volume customer. They point here rather than restating any of the
 * above:
 *
 * - **`packages/telemetry/src/improve-node-manipulation-errors.ts`** — wraps
 *   `Node.prototype.removeChild` / `insertBefore` / `appendChild` so *any* DOM-manipulation throw
 *   becomes an `AnnotatedDOMError` carrying an ancestor selector and a `domMethod` tag. This is what
 *   gives these Sentry issues the `AnnotatedDOMError` type in the first place. It is not
 *   translation-specific.
 * - **`packages/sentry-investigate/`** (+ `.github/workflows/sentry-investigate.yml`) — the hourly,
 *   repo-wide Sentry triage cron. It investigates every unresolved `AnnotatedDOMError` regardless of
 *   user count (`bin/sentry-investigate.mts`, pass 1) instead of applying the usual >=25-users/24h
 *   threshold. Investigation only; it never assigns. Its investigation agent is prompted to suggest
 *   {@link wrapBareText} / `TranslationSafeText` as the remedy.
 * - **`web/src/pods/errorHandling/components/AppTopLevelErrorBoundary.tsx`** — production
 *   containment: it string-matches the `removeChild` message, increments
 *   `web.error.removeChildFailed` and re-renders instead of tearing the app down. That is a safety
 *   net for sites not yet using this component, not an alternative to using it.
 */

import * as React from "react";

export type TranslationSafeTextProps = React.ComponentPropsWithoutRef<"span">;

/**
 * A plain `<span>` that gives React a stable element boundary around text an in-page translator
 * may rewrite. See the module documentation in this file for the full rationale, and
 * {@link wrapBareText} for the children-normalizing helper built on top of it.
 */
export const TranslationSafeText = React.forwardRef<HTMLSpanElement, TranslationSafeTextProps>((props, ref) => (
  <span ref={ref} {...props} />
));
TranslationSafeText.displayName = "TranslationSafeText";

function isBareText(node: React.ReactNode): node is string | number {
  return typeof node === "string" || typeof node === "number";
}

/**
 * Wraps bare `string`/`number` children in {@link TranslationSafeText}, leaving everything else
 * alone. Use this at the boundary where a component hands caller-supplied `children` to something
 * that may render them as a React-owned text node — see the module documentation for why.
 *
 * Element children are returned **by identity** (same keys, no extra wrapper) so nothing is
 * double-wrapped and element-only trees reconcile exactly as they did before this helper was
 * introduced. That identity guarantee is why detection is a separate pass: `React.Children.map`
 * re-keys every child it visits, so it only runs when there is bare text to wrap.
 *
 * `null`/`undefined` pass through unchanged, which matters for callers that distinguish "no
 * children" from "empty children" (radix's `SelectPrimitive.Value` keys `valueNodeHasChildren` off
 * `children !== undefined`).
 */
export function wrapBareText(children: React.ReactNode): React.ReactNode {
  if (isBareText(children)) return <TranslationSafeText>{children}</TranslationSafeText>;
  if (children == null) return children;

  let hasBareText = false;
  for (const child of React.Children.toArray(children)) {
    if (isBareText(child)) hasBareText = true;
  }
  if (!hasBareText) return children;

  return React.Children.map(children, (child) =>
    isBareText(child) ? <TranslationSafeText>{child}</TranslationSafeText> : child,
  );
}
